import { Component, Inject, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { ShortItem } from "../../shared/models/shortItem.model";
import { NextDueMaintenanceService } from "../next-due-maintenance.service";
import moment from "moment";
import { consumerPollProducersForChange } from "@angular/core/primitives/signals";
import { LocalStorageService } from "../../shared/local-storage.service";
import { AppConfigType } from "../../shared/models/appConfig.model";
import { HHMM } from "@rrsolutionsit/atl-utils";

@Component({
    selector: "app-update-next-due-maintenance-dialog",
    templateUrl: "./update-next-due-maintenance-dialog.component.html",
    styleUrls: ["./update-next-due-maintenance-dialog.component.scss"],
    standalone: false,
})
export class UpdateNextDueMaintenanceDialogComponent {
    @ViewChild("infoDialogTemplate") infoDialogTemplate;

    public nextDueMaintenanceFormGroup: FormGroup;
    public inspectionTypeOptions: { id: number; name: string }[];
    public showTolerance = false;
    public showFlightNumber = false;
    public showReference = false;
    public isRecurring = false;
    public registrationMark: string;
    public isCamo = false;
    private _userSession;
    public showEnginePowerCheck = false;
    public hookHoist = false;
    public hasHooks = false;
    public hasHoist = false;
    public showHours = true;
    public showCycles = true;

    public helicopterData;
    public helicopterHooks;
    public helicopterHoists;
    public totalHours: string;
    public totalCycles: number;
    public inspectionType: string;

    constructor(
        public dialogRef: MatDialogRef<UpdateNextDueMaintenanceDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        private _data: {
            helicopterId: number;
            shortItem: any;
            showTolerance: boolean;
            showFlightNumber: boolean;
            registrationMark: string;
            disableEnginePowerCheck: boolean;
            showReference: boolean;
            showEnginePowerCheck: boolean;
            hookHoist: boolean;
            helicopterData: any;
            hooks: any;
            hoists: any;
        },
        private _formBuilder: FormBuilder,
        private _snackBar: MatSnackBar,
        private _translate: TranslateService,
        private _nextDueMaintenanceService: NextDueMaintenanceService,
        private _localStorageService: LocalStorageService,
        private _dialog: MatDialog,
    ) {
        this.nextDueMaintenanceFormGroup = this._formBuilder.group({
            id: [""],
            inspectionTypeName: ["", Validators.required],
            inspectionTypeId: [""],
            reference: [""],
            status: [""],
            intervalHours: [{ value: null, disabled: true }, Validators.pattern(/^(0|[1-9][0-9]*|0[0-9]):[0-5][0-9]$/)],
            intervalDays: [{ value: null, disabled: true }, Validators.min(1)],
            intervalFlightNumber: [{ value: null, disabled: true }, Validators.min(1)],
            intervalCycles: [{ value: null, disabled: true }, Validators.min(1)],
            nextDueHours: [{ value: null, disabled: true }, Validators.pattern(/^(0|[1-9][0-9]*|0[0-9]):[0-5][0-9]$/)],
            nextDueDate: [{ value: null, disabled: true }],
            nextDueFlightNumber: [{ value: null, disabled: true }],
            nextDueCycles: [{ value: null, disabled: true }],
            toleranceHours: [{ value: null, disabled: true }, Validators.pattern(/^(0|[1-9][0-9]*|0[0-9]):[0-5][0-9]$/)],
            toleranceDays: [{ value: null, disabled: true }, Validators.min(0)],
            toleranceFlightNumber: [{ value: null, disabled: true }, Validators.min(0)],
            toleranceCycles: [{ value: null, disabled: true }, Validators.min(0)],
            type: [null, Validators.required],
            accessoryType: [""],
            helicopterId: [this._data.helicopterId],
            isEnginePowerCheck: [false],
            mode: [false],
        });
        this._nextDueMaintenanceService.getInspectionTypes().subscribe((result) => {
            this.inspectionTypeOptions = result;
        });
        if (this._data.shortItem) {
            this.nextDueMaintenanceFormGroup.patchValue(this._data.shortItem);
            this.nextDueMaintenanceFormGroup.get("inspectionTypeName").disable();
            if (this._data.shortItem.type === 1) {
                this.enableRecurring();
            } else {
                this.enableSingle();
            }
            if (this._data.shortItem.hookId && this._data.shortItem.hookId !== null) {
                this.nextDueMaintenanceFormGroup.get("accessoryType").setValue(0);
                this.nextDueMaintenanceFormGroup.get("accessoryType").disable();
                this.enableHook();
            }
            if (this._data.shortItem.hoistId && this._data.shortItem.hoistId !== null) {
                this.nextDueMaintenanceFormGroup.get("accessoryType").setValue(1);
                this.nextDueMaintenanceFormGroup.get("accessoryType").disable();
                this.enableHoist();
            }
        }

        this._userSession = this._localStorageService.getUserLocalStorageObject();

        if (this._data.showTolerance) {
            this.showTolerance = true;
        }

        if (this._data.showFlightNumber) {
            this.showFlightNumber = true;
        }

        if (this._data.registrationMark) {
            this.registrationMark = this._data.registrationMark;
        }

        if (this._data.disableEnginePowerCheck || this._data.shortItem?.mode) {
            this.nextDueMaintenanceFormGroup.get("isEnginePowerCheck").disable();
        }

        if (this._data.showReference) {
            this.showReference = this._data.showReference;
        }

        if (this._data.showEnginePowerCheck) {
            this.showEnginePowerCheck = this._data.showEnginePowerCheck;
        }

        if (this._data.shortItem?.isEnginePowerCheck && this._data.shortItem.status === 2) {
            this.nextDueMaintenanceFormGroup.get("status").disable();
        }

        if (this._data.hooks) {
            this.helicopterHooks = this._data.hooks;
        }

        if (this._data.hoists) {
            this.helicopterHoists = this._data.hoists;
        }

        this.hookHoist = this._data.hookHoist;

        if (this._data.helicopterData) {
            this.helicopterData = this._data.helicopterData;
            this.totalHours = HHMM.sumTimes(this.helicopterData?.totalHours, this.helicopterData?.flightSUM).value;
            this.totalCycles = this.helicopterData?.totalCycles + this.helicopterData?.currentCycles;
        }

        if (this.hookHoist) {
            if (this.helicopterHooks.length > 0) {
                this.hasHooks = true;
            }

            if (this.helicopterHoists.length > 0) {
                this.hasHoist = true;
            }
        }

        if (this.hasHooks && !this.hasHoist) {
            this.nextDueMaintenanceFormGroup.get("accessoryType").setValue("Hook");
            this.nextDueMaintenanceFormGroup.get("accessoryType").disable();
            this.enableHook();
        }

        if (this.hasHoist && !this.hasHooks) {
            this.nextDueMaintenanceFormGroup.get("accessoryType").setValue("Hoist");
            this.nextDueMaintenanceFormGroup.get("accessoryType").disable();
            this.enableHoist();
        }

        this.nextDueMaintenanceFormGroup.get("type").valueChanges.subscribe((value) => {
            if (value === 1) {
                // recurring task
                this.enableRecurring();
            } else if (value === 0) {
                // single task
                this.enableSingle();
            }
        });

        this.nextDueMaintenanceFormGroup.get("accessoryType").valueChanges.subscribe((value) => {
            if (value === 1) {
                // Hoist
                this.enableHoist();
            } else if (value === 0) {
                // Hook
                this.enableHook();
            }
        });

        this.nextDueMaintenanceFormGroup.get("reference").disable();

        if (this._userSession.htlAccountTypes.length > 0) {
            switch (this._userSession.htlAccountTypes[0].id) {
                case 1:
                case 2:
                    this.nextDueMaintenanceFormGroup.get("reference").enable();
                    break;
            }
        }
    }

    enableRecurring() {
        this.nextDueMaintenanceFormGroup.get("intervalDays").enable();
        this.nextDueMaintenanceFormGroup.get("intervalHours").enable();
        this.nextDueMaintenanceFormGroup.get("intervalFlightNumber").enable();
        this.nextDueMaintenanceFormGroup.get("intervalCycles").enable();
        this.nextDueMaintenanceFormGroup.get("nextDueDate").enable();
        this.nextDueMaintenanceFormGroup.get("nextDueHours").enable();
        this.nextDueMaintenanceFormGroup.get("nextDueFlightNumber").enable();
        this.nextDueMaintenanceFormGroup.get("nextDueCycles").enable();
        this.nextDueMaintenanceFormGroup.get("toleranceDays").enable();
        this.nextDueMaintenanceFormGroup.get("toleranceHours").enable();
        this.nextDueMaintenanceFormGroup.get("toleranceFlightNumber").enable();
        this.nextDueMaintenanceFormGroup.get("toleranceCycles").enable();
        this.isRecurring = true;
    }

    enableSingle() {
        this.nextDueMaintenanceFormGroup.get("intervalDays").disable();
        this.nextDueMaintenanceFormGroup.get("intervalHours").disable();
        this.nextDueMaintenanceFormGroup.get("intervalFlightNumber").disable();
        this.nextDueMaintenanceFormGroup.get("intervalCycles").disable();
        this.nextDueMaintenanceFormGroup.get("nextDueDate").enable();
        this.nextDueMaintenanceFormGroup.get("nextDueHours").enable();
        this.nextDueMaintenanceFormGroup.get("nextDueFlightNumber").enable();
        this.nextDueMaintenanceFormGroup.get("nextDueCycles").enable();
        this.nextDueMaintenanceFormGroup.get("toleranceDays").enable();
        this.nextDueMaintenanceFormGroup.get("toleranceHours").enable();
        this.nextDueMaintenanceFormGroup.get("toleranceFlightNumber").enable();
        this.nextDueMaintenanceFormGroup.get("toleranceCycles").enable();
        this.nextDueMaintenanceFormGroup.controls["intervalDays"].setValue(null);
        this.nextDueMaintenanceFormGroup.controls["intervalHours"].setValue(null);
        this.nextDueMaintenanceFormGroup.controls["intervalFlightNumber"].setValue(null);
        this.nextDueMaintenanceFormGroup.controls["intervalCycles"].setValue(null);
        this.isRecurring = false;
    }

    enableHook() {
        this.inspectionType = "hook";
        this.showHours = true;
        this.showCycles = true;
    }

    enableHoist() {
        this.inspectionType = "hoist";
        this.showHours = true;
        this.showCycles = true;
    }

    onAutocompleteClosed() {
        const optionValue = this.nextDueMaintenanceFormGroup.get("inspectionTypeName").value;
        const inspectionType = this.inspectionTypeOptions.filter((i) => i.name === optionValue)[0];
        if (inspectionType) {
            this.nextDueMaintenanceFormGroup.patchValue({ inspectionTypeId: inspectionType.id });
        } else {
            this.nextDueMaintenanceFormGroup.patchValue({ inspectionTypeId: null });
        }
    }

    onFormSubmit() {
        this.nextDueMaintenanceFormGroup.setErrors(null);
        if (this.nextDueMaintenanceFormGroup.valid) {
            if (this.nextDueMaintenanceFormGroup.get("type").value !== 0) {
                if (
                    !this.nextDueMaintenanceFormGroup.get("intervalDays").value &&
                    !this.nextDueMaintenanceFormGroup.get("intervalHours").value &&
                    !this.nextDueMaintenanceFormGroup.get("intervalFlightNumber").value &&
                    !this.nextDueMaintenanceFormGroup.get("intervalCycles").value
                ) {
                    this.nextDueMaintenanceFormGroup.setErrors({ noInterval: true });
                    return;
                }
            }
            if (!this.isRecurring) {
                if (
                    !this.nextDueMaintenanceFormGroup.get("nextDueDate").value &&
                    !this.nextDueMaintenanceFormGroup.get("nextDueHours").value &&
                    !this.nextDueMaintenanceFormGroup.get("nextDueFlightNumber").value &&
                    !this.nextDueMaintenanceFormGroup.get("nextDueCycles").value
                ) {
                    this.nextDueMaintenanceFormGroup.setErrors({ missingNextDue: true });
                    return;
                }
            }

            const obj = this.nextDueMaintenanceFormGroup.getRawValue();
            if (obj.nextDueDate) {
                obj.nextDueDate = moment(obj.nextDueDate).format("YYYY-MM-DD");
            }
            if (obj.mode) {
                obj.mode = obj.mode ? 2 : 1;
            }
            let obs: Observable<any>;
            if (this.hookHoist) {
                if (obj.accessoryType === "Hook") {
                    obj.accessoryType = 0;
                }
                if (obj.accessoryType === "Hoist") {
                    obj.accessoryType = 1;
                }
                if (obj.accessoryType === 0) {
                    obj.hookId = this.helicopterHooks[0].id;
                } else {
                    obj.hoistId = this.helicopterHoists[0].id;
                }
                if (!this._data.shortItem) {
                    obs = this._nextDueMaintenanceService.insertAccessory(obj);
                } else {
                    obs = this._nextDueMaintenanceService.updateAccessory(obj);
                }
            } else {
                if (!this._data.shortItem) {
                    obs = this._nextDueMaintenanceService.insertShortItem(obj);
                } else {
                    obs = this._nextDueMaintenanceService.updateShortItem(obj);
                }
            }
            obs.subscribe(() => {
                this.dialogRef.close();
                this._snackBar.open(this._translate.instant("GENERAL.savedSuccessMsg"), this._translate.instant("GENERAL.close"), {
                    duration: 3000,
                });
            });
        } else {
            this.nextDueMaintenanceFormGroup.markAllAsTouched();
        }
    }

    getHookHoistLabel() {
        if (this.nextDueMaintenanceFormGroup.get("accessoryType").value === 0 && this.helicopterHooks.length > 0) {
            return "/ " + this.helicopterHooks[0].serial;
        }
        if (this.nextDueMaintenanceFormGroup.get("accessoryType").value === 1 && this.helicopterHoists.length > 0) {
            return "/ " + this.helicopterHoists[0].serial;
        }
        return "";
    }

    onModeChange(event) {
        if (event.checked) {
            this.nextDueMaintenanceFormGroup.get("isEnginePowerCheck").disable();
            this.nextDueMaintenanceFormGroup.get("isEnginePowerCheck").setValue(false);
        } else if (!this._data.disableEnginePowerCheck) {
            this.nextDueMaintenanceFormGroup.get("isEnginePowerCheck").enable();
        }
    }

    infoButtonClicked() {
        const dialogRef = this._dialog.open(this.infoDialogTemplate, {
            width: "600px",
            disableClose: true,
        });
    }
}
